import {
	useMemo,
	useState,
	useImperativeHandle,
	forwardRef,
	useEffect,
	useCallback
} from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';

import { ListPageLayout } from '@/components/layout/list-page-layout';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showDeleteColumn } from '@/lib/featureFlags';

import { UserDetails, UserDetailsHeader } from './user-details';
import { USERS_LIST_QUERY } from './users.gql.tsx';
import {
	selectUserAfterSave,
	type UserListRecord
} from './user-list-selection';

interface UsersListResponse {
	users?: {
		nodes?: UserListRecord[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		};
	} | null;
}

export interface UsersListRef {
	selectUserAfterSave: (userId: string) => Promise<void>;
}

const formatFullName = (user?: UserListRecord) => {
	if (!user) return '';

	const parts = [user.firstName, user.lastName].filter(
		(part): part is string => Boolean(part?.trim())
	);

	if (parts.length > 0) {
		return parts.join(' ');
	}

	return user.username ?? user.email ?? 'Unnamed user';
};

const formatCreatedDate = (value: string | undefined) => {
	if (!value) {
		return 'N/A';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleDateString();
};

const formatRoleNames = (roles?: UserListRecord['userRoles']) => {
	if (!roles || roles.length === 0) {
		return 'No roles';
	}

	const names = roles.map(role => role.role?.name?.trim()).filter(Boolean);

	if (names.length === 0) {
		return `Roles assigned (${roles.length})`;
	}

	return names.join(', ');
};

export const UsersList = forwardRef<UsersListRef>((_props, ref) => {
	const { t } = useTranslation('users');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedUser, setSelectedUser] = useState<UserListRecord | null>(
		null
	);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const [localUsers, setLocalUsers] = useState<UserListRecord[]>([]);
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);
	const { goToCreate, goToEdit } = useResourceNavigation('users');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();

	const { data, loading, refetch, fetchMore } =
		useQuery<UsersListResponse>(USERS_LIST_QUERY, {
			variables: { first: itemsToFetch },
			fetchPolicy: 'cache-and-network'
		});

	// Pagination state
	const pageInfo = data?.users?.pageInfo;
	const hasNextPage = pageInfo?.hasNextPage ?? false;
	const endCursor = pageInfo?.endCursor;
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					const newNodes = fetchMoreResult.users?.nodes || [];
					const prevNodes = prev.users?.nodes || [];
					setItemsToFetch(prevNodes.length + newNodes.length);
					setLocalUsers([]);
					return {
						...prev,
						users: {
							...fetchMoreResult.users,
							nodes: [...prevNodes, ...newNodes]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, endCursor, fetchMore]);

	const handleRefetch = useCallback(() => {
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		setLocalUsers([]);
		refetch({ first: DEFAULT_PAGE_SIZE });
	}, [refetch]);

	// Sync local users with server data
	const users = useMemo(() => {
		const serverUsers = data?.users?.nodes ?? [];
		// Always prefer server data - local state is only for optimistic updates
		if (localUsers.length === 0 || serverUsers.length === 0) {
			return serverUsers;
		}
		// Use local state only when both have data (for optimistic updates)
		return localUsers;
	}, [data?.users?.nodes, localUsers]);

	// Expose method to select user after save via ref
	useImperativeHandle(
		ref,
		() => ({
			selectUserAfterSave: async (userId: string) => {
				await selectUserAfterSave({
					userId,
					client: apolloClient,
					currentUsers: users,
					onDataUpdate: setLocalUsers,
					onUserSelect: setSelectedUser
				});
			}
		}),
		[apolloClient, users]
	);

	// Handle user selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectUserId?: string } | null;
		if (state?.selectUserId && users.length > 0 && !loading) {
			const userIdToSelect = state.selectUserId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectUserAfterSave({
						userId: userIdToSelect,
						client: apolloClient,
						currentUsers: users,
						onDataUpdate: setLocalUsers,
						onUserSelect: setSelectedUser
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select user after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		users.length,
		loading,
		apolloClient,
		users,
		navigate,
		location.pathname
	]);

	useEffect(() => {
		if (!selectedUser && users.length > 0) {
			setSelectedUser(users[0]);
			return;
		}

		if (selectedUser) {
			const updated = users.find(
				user => user.userId === selectedUser.userId
			);

			if (!updated) {
				setSelectedUser(null);
				return;
			}

			if (updated !== selectedUser) {
				setSelectedUser(updated);
			}
		}
	}, [selectedUser, users]);

	const handleDeleteUser = async (user: UserListRecord) => {
		const userName = formatFullName(user);
		const confirmed = await showDialog(
			'Delete User',
			`Are you sure you want to delete user "${userName}"? This action cannot be undone.`
		);

		if (confirmed) {
			setLocalUsers([]);
			// Placeholder for delete functionality - will be implemented later
			console.log('Delete user:', user.userId);
		}
	};

	const handleDeleteUserById = async (userId: string) => {
		const user = users.find(u => u.userId === userId);
		if (user) {
			await handleDeleteUser(user);
		}
	};

	const handleCloneUser = (userId: string) => {
		const user = users.find(u => u.userId === userId);
		if (user) {
			// Navigate to create page with cloned user data
			go({
				to: '/users/create',
				state: {
					cloneData: {
						firstName: user.firstName ?? '',
						lastName: user.lastName ?? '',
						email: user.email ?? '',
						username: user.username ?? '',
						isActive: user.isActive,
						roleIds: user.userRoles?.map(ur => String(ur.role?.roleId)).filter(Boolean) ?? []
					}
				}
			});
		}
	};

	const columns: ColDef<UserListRecord>[] = [
		{
			headerName: t('list.columns.name'),
			field: 'firstName',
			valueGetter: params => formatFullName(params.data),
			minWidth: 180
		},
		{
			headerName: t('list.columns.email'),
			field: 'email',
			minWidth: 200,
			flex: 1,
			valueGetter: params => params.data?.email ?? 'N/A'
		},
		{
			headerName: t('list.columns.username'),
			field: 'username',
			minWidth: 160,
			valueGetter: params => params.data?.username ?? 'N/A'
		},
		{
			headerName: t('list.columns.status'),
			field: 'isActive',
			width: 120,
			minWidth: 120,
			cellRenderer: ActiveInactiveCellRenderer
		},
		{
			headerName: t('list.columns.roles'),
			field: 'userRoles',
			minWidth: 200,
			flex: 1,
			valueGetter: params => formatRoleNames(params.data?.userRoles)
		},
		{
			headerName: t('list.columns.created'),
			field: 'createdAt',
			minWidth: 140,
			valueGetter: params => formatCreatedDate(params.data?.createdAt)
		},
		...(showDeleteColumn ? [{
			headerName: '',
			field: undefined,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			cellRenderer: DeleteCellRenderer,
			cellRendererParams: {
				onDelete: handleDeleteUser
			},
			sortable: false,
			filter: false,
			resizable: false
		}] : [])
	];

	const handleRowClick = (user: UserListRecord) => {
		setSelectedUser(user);
	};

	return (
		<ListPageLayout<UserListRecord>
			gridId="users-list"
			columns={columns}
			data={users}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={loading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefetch}
			selectedItem={selectedUser}
			detailsTitle="User details"
			onEdit={id => goToEdit(id)}
			onDelete={handleDeleteUserById}
			onOpenInNewWindow={id => openEntityInNewWindow('users', id)}
			onClone={handleCloneUser}
			renderDetailsContent={item => (
				<UserDetails
					userId={item.userId}
					activeTab={activeDetailsTab}
					onTabChange={setActiveDetailsTab}
					hideTabsList={true}
				/>
			)}
			renderDetailsHeader={(user, actions) => (
				<UserDetailsHeader
					user={user}
					{...actions}
				/>
			)}
			getRecordId={item => item.userId}
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
});
